import TicketOfficeClearAPIs from "APIs/ticket-office-clear";
import CustomDatePicker from "Components/CustomDatePicker";
import Footer from "Components/Footer";
import Heading from "Components/Heading";
import { format } from "date-fns";
import { formatDate } from "Helper/Converters";
import PayrollLogModal from "Pages/HoursLog/components/PayrollLogModal";
import PerTicketReportModal from "Pages/HoursLog/components/PerTicketReportModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { BiBookAdd, BiSearch } from "react-icons/bi";
import { MdDateRange } from "react-icons/md";
import { useSelector } from "react-redux";
import Select from "react-select";
import moment from "moment";
import { exportFileToExcel } from "Utils/utils";
import AuthAPIs from "../../APIs/auth";
import HourLogAPIs from "../../APIs/hour-log";

const Payroll = () => {
  const { userId } = useSelector((state) => state.auth);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState();
  const [selectedPayrollRegion, setSelectedPayrollRegion] = useState({
    value: "WATERLOO",
    label: "Waterloo",
  });

  const [hourLogs, setHourLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [
    approveDisapproveHourLogModalShow,
    setApproveDisapproveHourLogModalShow,
  ] = useState(false);
  const [logDate, setLogDate] = useState(format(new Date(), "yyyy-MM-dd")); // Default to current date

  const [perTicketReportModalShow, setPerTicketReportModalShow] =
    useState(false);
  const [officeClearModalShow, setOfficeClearModalShow] = useState(false);
  const [officeClearCount, setOfficeClearCount] = useState(null);
  const [officeClearTickets, setOfficeClearTickets] = useState([]);
  const [utilitiesOfficeCleared, setUtilitiesOfficeCleared] = useState([]);
  const [perTicketReportData, setPerTicketReportData] = useState([]);
  const [hourLogTicketDetails, setHourLogTicketDetails] = useState([]);
  const [driveWayCurboxModalShow, setDriveWayCurboxModalShow] = useState(false);

  const HALTON_REGION_VALUE = "HALTON REGION";
  const driveWayCurboxData =
    perTicketReportData?.haltonRegion?.driveWayCurbox ?? [];

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "44px",
    }),
  };

  const payrollRegionOptions = [
    { value: "WATERLOO", label: "Waterloo" },
    { value: "YORK", label: "York" },
    { value: "HALTON HILLS", label: "Halton Hills" },
    { value: "HALTON REGION", label: "Halton Region" },
  ];

  // Convert Date object to desired format
  const customFormatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  };

  /**
   * Get All Users.
   */
  const getUsers = useCallback(async () => {
    const usersResponse = await AuthAPIs.getAllUsers();
    const customArr = [{ value: "", label: "Select" }];
    if (usersResponse) {
      usersResponse.data.data.forEach((item) => {
        // Only include active users
        if (item?.isActive === true) {
          customArr.push({ value: item.id, label: item.firstName });
        }
      });
      customArr.sort((a, b) => {
        if (a.label.toLowerCase() < b.label.toLowerCase()) {
          return -1;
        }
        if (a.label.toLowerCase() > b.label.toLowerCase()) {
          return 1;
        }
        return 0;
      });
      setUsers([{ value: "", label: "Select" }, ...customArr]);
    }
  }, [userId]);

  useEffect(() => {
    if (users.length === 0) getUsers();
  }, [getUsers, users.length]);

  const exportToExcel = (data, fileName) => {
    exportFileToExcel(data, fileName);
  };

  const getHourLogReport = async () => {
    if (!selectedUser?.value || !startDate || !endDate) {
      alert("Please select a user and both dates.");
      return;
    }

    setIsLoading(true);

    // Format dates
    const formattedStartDate = customFormatDate(startDate);
    const formattedEndDate = customFormatDate(endDate);
    // Make Date To inclusive by adding 1 day
    const endDatePlusOne_export = new Date(startDate);
    endDatePlusOne_export.setDate(endDatePlusOne_export.getDate() + 1);

    // Dummy API call structure, replace with your actual API call
    try {
      const response = await HourLogAPIs.getPayrollReport(
        selectedUser.value,
        formattedEndDate,
        formattedStartDate,
      );

      setHourLogs(
        response.data.data.map((item) => {
          return {
            ...item,
            totalHours: Number(Number(item.totalHours).toFixed(1)),
            approvedHours: Number(Number(item.approvedHours).toFixed(1)),
          };
        }),
      );

      const ticketRes = await HourLogAPIs.getHoursLogTicketReport(
        selectedUser.value,
        formattedEndDate,
        formattedStartDate,
        true,
        selectedPayrollRegion.value,
      );
      setPerTicketReportData(ticketRes?.data?.data?.calculatedPayroll);
      setHourLogTicketDetails(ticketRes?.data?.data?.ticketDetails);
    } catch (error) {
      console.error("Failed to fetch hour logs:", error);
      alert("An error occurred while fetching hour logs.");
    } finally {
      setIsLoading(false);
    }
  };

  const generateHoursLogTicketReport = async (ticketDetails) => {
    try {
      const filtered = ticketDetails?.filter((el) => {
        return el.logType === "PER_TICKET" || el.logType === "AHE_TICKET";
      });

      // console.log("filtered data", filtered)
      const toExportValue = (value) => {
        if (value === null || value === undefined || value === "") return 0;
        if (value === "NaN") return 0;
        const asNumber = Number(value);
        return Number.isNaN(asNumber) ? value : asNumber;
      };

      const PRE_COMPLETE_FIELDS = [
        { key: "bramptonStormUnit", label: "Brampton Storm Unit" },
        { key: "electricalUnit", label: "Electrical Unit" },
        { key: "peelStormUnit", label: "Peel Storm Unit" },
        // Halton-specific & resurfacing fields (present in API response)
        { key: "resurfaced", label: "Resurfaced" },
        { key: "driveWayCurbox", label: "Driveway Curbox" },

        // RW fields (present in API response)
        { key: "rwCLWaterUnit", label: "RW CL Water Unit" },
        { key: "rwCLSanitaryUnit", label: "RW CL Sanitary Unit" },
        { key: "rwCLStormUnit", label: "RW CL Storm Unit" },
        { key: "rwMKWaterUnit", label: "RW MK Water Unit" },
        { key: "rwMKSanitaryUnit", label: "RW MK Sanitary Unit" },
        { key: "rwMKStormUnit", label: "RW MK Storm Unit" },
        { key: "rwWorkArea", label: "RW Work Area" },

        // Utilities with Unit/Clear/Additional should stay grouped side-by-side
        { key: "waterUnit", label: "Water Unit" },
        { key: "waterUnitClear", label: "Water Unit Clear" },
        { key: "waterUnitAdditional", label: "Water Unit Additional" },

        { key: "sanitaryUnit", label: "Sanitary Unit" },
        { key: "sanitaryUnitClear", label: "Sanitary Unit Clear" },
        { key: "sanitaryUnitAdditional", label: "Sanitary Unit Additional" },

        { key: "sewerUnit", label: "Sewer Unit" },
        { key: "sewerUnitClear", label: "Sewer Unit Clear" },
        { key: "sewerUnitAdditional", label: "Sewer Unit Additional" },

        { key: "gasUnit", label: "Gas Unit" },
        { key: "gasUnitClear", label: "Gas Unit Clear" },
        { key: "gasUnitAdditional", label: "Gas Unit Additional" },

        { key: "hydroUnit", label: "Hydro Unit" },
        { key: "hydroUnitClear", label: "Hydro Unit Clear" },
        { key: "hydroUnitAdditional", label: "Hydro Unit Additional" },

        { key: "telecomUnit", label: "Telecom Unit" },
        { key: "telecomUnitClear", label: "Telecom Unit Clear" },
        { key: "telecomUnitAdditional", label: "Telecom Unit Additional" },

        { key: "catvUnit", label: "CATV Unit" },
        { key: "catvUnitClear", label: "CATV Unit Clear" },
        { key: "catvUnitAdditional", label: "CATV Unit Additional" },

        { key: "otherUnit", label: "Other Unit" },
        { key: "otherUnitClear", label: "Other Unit Clear" },
        { key: "otherUnitAdditional", label: "Other Unit Additional" },

        { key: "streetLightUnit", label: "Street Light Unit" },
        { key: "streetLightUnitClear", label: "Street Light Unit Clear" },
        {
          key: "streetLightUnitAdditional",
          label: "Street Light Unit Additional",
        },

        { key: "trafficLightUnit", label: "Traffic Light Unit" },
        { key: "trafficLightUnitClear", label: "Traffic Light Unit Clear" },
        {
          key: "trafficLightUnitAdditional",
          label: "Traffic Light Unit Additional",
        },

        { key: "zumUnit", label: "Zum Unit" },
        { key: "zumUnitClear", label: "Zum Unit Clear" },
        { key: "zumUnitAdditional", label: "Zum Unit Additional" },

        // Storm / Fiber / Waterwaste / Traffic signal & comm (present in API response)
        { key: "stormUnit", label: "Storm Unit" },
        { key: "stormUnitClear", label: "Storm Unit Clear" },
        { key: "stormUnitAdditional", label: "Storm Unit Additional" },

        { key: "fiberUnit", label: "Fiber Unit" },
        { key: "fiberUnitClear", label: "Fiber Unit Clear" },
        { key: "fiberUnitAdditional", label: "Fiber Unit Additional" },

        { key: "waterwasteUnit", label: "Waterwaste Unit" },
        { key: "waterwasteUnitClear", label: "Waterwaste Unit Clear" },
        {
          key: "waterwasteUnitAdditional",
          label: "Waterwaste Unit Additional",
        },

        { key: "trafficSignalUnit", label: "Traffic Signal Unit" },
        { key: "trafficSignalUnitClear", label: "Traffic Signal Unit Clear" },
        {
          key: "trafficSignalUnitAdditional",
          label: "Traffic Signal Unit Additional",
        },

        { key: "trafficComUnit", label: "Traffic Com Unit" },
        { key: "trafficComUnitClear", label: "Traffic Com Unit Clear" },
        {
          key: "trafficComUnitAdditional",
          label: "Traffic Com Unit Additional",
        },
      ];

      const HEADINGS = [
        {
          v: "Entered Date",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "Ticket Number",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "Ticket Type",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "Ticket Priority",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "Station Code",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "Type of Work",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        ...PRE_COMPLETE_FIELDS.map((field) => ({
          v: field.label,
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        })),
        {
          v: "Per Ticket",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "AHE Ticket",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
      ];
      const data = filtered?.map((item) => [
        { v: new Date(formatDate(item.createdAt)).toLocaleDateString() },
        { v: item.ticketNo },
        { v: item.ticketType },
        { v: item.priority },
        { v: item.stationCodes?.map((it) => it?.name).join(" ") },
        { v: item.typeOfWork },
        ...PRE_COMPLETE_FIELDS.map((field) => ({
          v: toExportValue(item.ticketPreComplete?.[field.key]),
        })),
        { v: item.logType == "PER_TICKET" ? "Yes" : "No" },
        { v: item.logType == "AHE_TICKET" ? "Yes" : "No" },
      ]);

      exportToExcel(
        [HEADINGS, ...data],
        `${selectedUser?.label} Ticket Report ${
          new Date(startDate).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }) +
          "-" +
          new Date(endDate).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        }`,
      );
    } catch (error) {
      console.log("Failed To Export Report");
    }
  };

  const locatorOfficeClearCount = async () => {
    if (!selectedUser?.value || !startDate || !endDate) {
      alert("Please select a user and both dates.");
      return;
    }

    try {
      const formattedStartDate = customFormatDate(startDate);
      const formattedEndDate = customFormatDate(endDate);

      const totalRes = await TicketOfficeClearAPIs.locator_office_clear_count({
        userId: selectedUser.value,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
      const payload = totalRes?.data?.data || totalRes?.data || {};
      const locator_office_clear_count = Number(payload?.count || 0);
      const tickets = payload?.tickets || [];
      const utilitiesCleared = payload?.utilitiesOfficeCleared || [];
      setOfficeClearCount(locator_office_clear_count);
      setOfficeClearTickets(tickets);
      setUtilitiesOfficeCleared(utilitiesCleared);
      setOfficeClearModalShow(true);
    } catch (e) {
      console.error(e);
      alert("Failed to fetch office clear stats.");
    }
  };

  const exportTicketsToExcel = () => {
    if (!officeClearTickets || officeClearTickets.length === 0) {
      alert("No tickets to export");
      return;
    }

    const formattedData = [
      [
        {
          v: "Office Clear Tickets Report",
          t: "s",
          s: { font: { bold: true, sz: 18, color: { rgb: "0068af" } } },
        },
      ],
      [
        {
          v: `User: ${selectedUser?.label || "N/A"}`,
          t: "s",
          s: { font: { bold: true } },
        },
      ],
      [
        {
          v: `Date Range: ${
            endDate ? new Date(endDate).toLocaleDateString("en-GB") : "N/A"
          } to ${
            startDate ? new Date(startDate).toLocaleDateString("en-GB") : "N/A"
          }`,
          t: "s",
          s: { font: { bold: true } },
        },
      ],
      [
        {
          v: `Total Tickets: ${officeClearTickets.length}`,
          t: "s",
          s: { font: { bold: true } },
        },
      ],
      [],
      [
        {
          v: "Ticket Number",
          t: "s",
          s: { font: { bold: true, sz: 14, color: { rgb: "0068af" } } },
        },
      ],
    ];

    // Add ticket data
    officeClearTickets.forEach((ticketNo, index) => {
      formattedData.push([
        {
          v: ticketNo,
          s: { font: { bold: true, color: { rgb: "1976d2" } } },
        },
      ]);
    });

    // Utilities Office Cleared (additive)
    formattedData.push([]);
    formattedData.push([
      {
        v: "Utilities Office Cleared",
        t: "s",
        s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
      },
    ]);
    if (utilitiesOfficeCleared.length > 0) {
      formattedData.push([
        { v: "Ticket #", t: "s", s: { font: { bold: true } } },
        { v: "Station Code", t: "s", s: { font: { bold: true } } },
        { v: "Utility", t: "s", s: { font: { bold: true } } },
        { v: "Status", t: "s", s: { font: { bold: true } } },
        { v: "Cleared At", t: "s", s: { font: { bold: true } } },
      ]);
      utilitiesOfficeCleared.forEach((row) => {
        formattedData.push([
          { v: row?.ticketNo ?? "-" },
          { v: row?.stationCode ?? "-" },
          { v: row?.utilityType ?? "-" },
          { v: row?.status ?? "-" },
          {
            v: row?.createdAt
              ? moment(
                  new Date(row.createdAt).toLocaleString("en-US", {
                    timeZone: "America/Toronto",
                  }),
                ).format("DD MMM YYYY hh:mm A")
              : "-",
          },
        ]);
      });
    } else {
      formattedData.push([
        { v: "No utilities office cleared in selected date range." },
      ]);
    }

    const fileName = `Office_Clear_Tickets_${selectedUser?.label || "User"}_${
      new Date().toISOString().split("T")[0]
    }`;
    exportFileToExcel(formattedData, fileName);
  };

  const showTotal = useMemo(() => {
    const TotalInitial = {
      totalHours: 0.0,
      totalHourLogCount: 0.0,
      perTicketCount: 0,
      aheTicketCount: 0,
      reimbursementCount: 0,
      approvedHours: 0.0,
      reimbursementTotalPrice: 0.0,
    };
    const total = hourLogs.reduce((total, item) => {
      return {
        totalHours: total.totalHours + item.totalHours,
        totalHourLogCount: total.totalHourLogCount + item.hourLogCount,
        perTicketCount: total.perTicketCount + item.perTicketCount,
        aheTicketCount: total.aheTicketCount + item.aheTicketCount,
        reimbursementCount: total.reimbursementCount + item.reimbursementCount,
        reimbursementTotalPrice:
          total.reimbursementTotalPrice + item.reimbursementTotalPrice,
        approvedHours: total.approvedHours + item.approvedHours,
      };
    }, TotalInitial);
    return (
      <tr>
        <td>Total</td>
        <td>{Number(total.totalHours).toFixed(1)}</td>
        <td>{total.totalHourLogCount}</td>
        <td>{total.perTicketCount}</td>
        <td>{total.aheTicketCount}</td>
        <td>0.0</td>
        <td>0.0</td>
        <td>{total.reimbursementCount}</td>
        <td>{Number(total.reimbursementTotalPrice).toFixed(2)}</td>
        <td>{Number(total.approvedHours).toFixed(1)}</td>
      </tr>
    );
  }, [hourLogs]);
  const handleClosePayrollLogModal = () => {
    // Close without refetching the entire report; avoid unnecessary API call on modal close
    setApproveDisapproveHourLogModalShow(false);
  };
  return (
    <>
      <section>
        <Heading text={"Payroll Audit"} />

        <Card>
          <Form className="dashboard-form mb-4">
            <Row>
              <Col className="column">
                <Form.Group className="form-group">
                  <Form.Label>Users</Form.Label>
                  <Select
                    options={users}
                    styles={{
                      ...customStyles,
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Adjust zIndex value as needed
                    }}
                    menuPortalTarget={document.body}
                    defaultValue={users[0]}
                    onChange={(selectedOption) => {
                      setSelectedUser(selectedOption);
                    }}
                  />
                </Form.Group>
              </Col>
              <Col className="column">
                <Form.Group className="form-group">
                  <Form.Label>Region</Form.Label>
                  <Select
                    options={payrollRegionOptions}
                    styles={{
                      ...customStyles,
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Adjust zIndex value as needed
                    }}
                    menuPortalTarget={document.body}
                    value={selectedPayrollRegion}
                    onChange={(selectedOption) => {
                      setSelectedPayrollRegion(selectedOption);
                    }}
                  />
                </Form.Group>
              </Col>

              <Row className="pe-0">
                <Col className="column">
                  <Form.Group className="form-group">
                    <Form.Label>Date From</Form.Label>
                    <div className="datepicker_box">
                      <CustomDatePicker
                        selected={endDate}
                        onSelect={(date) => setEndDate(date)}
                        dateFormat="yyyy-MM-dd" // Adjust the date format as needed
                        popperPlacement="right-start"
                      />
                      <span>
                        <MdDateRange />
                      </span>
                    </div>
                  </Form.Group>
                </Col>
                <Col className="column">
                  <Form.Group className="form-group">
                    <Form.Label>Date To</Form.Label>
                    <div className="datepicker_box">
                      <CustomDatePicker
                        selected={startDate}
                        onSelect={(date) => setStartDate(date)}
                        dateFormat="yyyy-MM-dd" // Adjust the date format as needed
                        popperPlacement="right-start"
                      />
                      <span>
                        <MdDateRange />
                      </span>
                    </div>
                  </Form.Group>
                </Col>
                <Col
                  className="mb-4 column"
                  style={{ maxWidth: "120px", padding: "0" }}
                >
                  <Button
                    style={{ minHeight: "44px", marginTop: "22px" }}
                    onClick={getHourLogReport}
                  >
                    <BiSearch />
                    Search
                  </Button>
                </Col>
                <Col md={12}>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      onClick={() => {
                        if (!selectedUser?.value || !startDate || !endDate) {
                          alert("Please select a user and both dates.");
                          return;
                        }
                        if (!(hourLogs.length > 0)) {
                          alert("No Data Exists!");
                          return;
                        }
                        const HEADINGS = [
                          {
                            v: "Entered Date",
                            t: "s",
                            s: {
                              font: {
                                bold: true,
                                sz: 16,
                                color: { rgb: "0068af" },
                              },
                            },
                          },
                          {
                            v: "Approved Hours",
                            t: "s",
                            s: {
                              font: {
                                bold: true,
                                sz: 16,
                                color: { rgb: "0068af" },
                              },
                            },
                          },
                          {
                            v: "OT Hours",
                            t: "s",
                            s: {
                              font: {
                                bold: true,
                                sz: 16,
                                color: { rgb: "0068af" },
                              },
                            },
                          },
                          {
                            v: "STAT Hours",
                            t: "s",
                            s: {
                              font: {
                                bold: true,
                                sz: 16,
                                color: { rgb: "0068af" },
                              },
                            },
                          },
                          {
                            v: "AHE Hours",
                            t: "s",
                            s: {
                              font: {
                                bold: true,
                                sz: 16,
                                color: { rgb: "0068af" },
                              },
                            },
                          },
                          {
                            v: "AHE Count",
                            t: "s",
                            s: {
                              font: {
                                bold: true,
                                sz: 16,
                                color: { rgb: "0068af" },
                              },
                            },
                          },
                          {
                            v: "Per-Ticket Count",
                            t: "s",
                            s: {
                              font: {
                                bold: true,
                                sz: 16,
                                color: { rgb: "0068af" },
                              },
                            },
                          },
                          {
                            v: "Hourlog Ticket Count",
                            t: "s",
                            s: {
                              font: {
                                bold: true,
                                sz: 16,
                                color: { rgb: "0068af" },
                              },
                            },
                          },
                        ];
                        const data = hourLogs.map((item) => [
                          {
                            v: new Date(
                              formatDate(item.date),
                            ).toLocaleDateString(),
                          },
                          { v: Number(item.approvedHours).toFixed(1) },
                          { v: "0.0" },
                          { v: "0.0" },
                          { v: "0.0" },
                          { v: item.aheTicketCount },
                          { v: item.perTicketCount },
                          { v: item.hourLogCount },
                        ]);
                        exportToExcel(
                          [HEADINGS, ...data],
                          `${selectedUser?.label} Hour Log Report ${
                            new Date(startDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            }) +
                            "-" +
                            new Date(endDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          }`,
                        );
                      }}
                    >
                      <BiBookAdd />
                      Create Hour Log Report
                    </Button>
                    <Button
                      onClick={() =>
                        generateHoursLogTicketReport(hourLogTicketDetails)
                      }
                    >
                      <BiBookAdd />
                      Create Tickets Report
                    </Button>
                    {/* <Button onClick={getHoursLogPreTicketReport}>
                    <BiBookAdd />
                    Create Pre-Tickets Report
                  </Button> */}

                    <Button onClick={() => setPerTicketReportModalShow(true)}>
                      <BiBookAdd />
                      Per Ticket Report
                    </Button>
                    <Button onClick={locatorOfficeClearCount}>
                      <BiBookAdd />
                      Office Clear Count
                    </Button>
                    {selectedPayrollRegion?.value === HALTON_REGION_VALUE && (
                      <Button onClick={() => setDriveWayCurboxModalShow(true)}>
                        <BiBookAdd />
                        Driveway Curbox
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </Row>
          </Form>
          <Row>
            {isLoading ? (
              <p>Loading...</p>
            ) : hourLogs.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Total Hours</th>
                    <th>Hourlog Ticket Count</th>
                    <th>Per Ticket Count</th>
                    <th>AHE Ticket Count</th>
                    <th>Stat Holiday Hours</th>
                    <th>OT Total Hours</th>
                    <th>Reimbursement Count</th>
                    <th>Reimbursements Price</th>
                    <th>Approved Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {hourLogs.map((log, index) => (
                    <tr key={index}>
                      <td
                        className="link-primary"
                        role="button"
                        onClick={() => {
                          setLogDate(log.date);
                          setApproveDisapproveHourLogModalShow(true);
                        }}
                      >
                        {new Date(log.date).toLocaleDateString()}
                      </td>
                      <td>{log.totalHours}</td>
                      <td>{log.hourLogCount}</td>
                      <td>{log.perTicketCount}</td>
                      <td>{log.aheTicketCount}</td>
                      <td>0.0</td>
                      <td>0.0</td>
                      <td>{log.reimbursementCount}</td>
                      <td>
                        {parseFloat(log.reimbursementTotalPrice).toFixed(2)}
                      </td>
                      <td>{log.approvedHours}</td>
                    </tr>
                  ))}
                  {showTotal}
                </tbody>
              </table>
            ) : (
              <p>No hour log data available.</p>
            )}
          </Row>
          <Footer />
          <PayrollLogModal
            show={approveDisapproveHourLogModalShow}
            handleClose={handleClosePayrollLogModal}
            logDate={logDate}
            setLogDate={setLogDate}
            selectedUserId={selectedUser?.value}
          />
          <PerTicketReportModal
            show={perTicketReportModalShow}
            handleClose={() => setPerTicketReportModalShow(false)}
            data={perTicketReportData}
            user={{
              name: selectedUser?.label,
              endDate: endDate,
              startDate: startDate,
            }}
            region={selectedPayrollRegion}
          />
          <Modal
            show={officeClearModalShow}
            onHide={() => setOfficeClearModalShow(false)}
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                Locator Office Clears - {selectedUser?.label} {" ("}
                {formatDate(new Date(endDate), "dd/MM/yyyy")} -{" "}
                {formatDate(new Date(startDate), "dd/MM/yyyy")}
                {")"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="border rounded p-3">
                <Row>
                  <Col md={6} className="">
                    <h5>Total Office Clears: {officeClearCount}</h5>
                  </Col>
                </Row>
                {officeClearTickets.length > 0 && (
                  <Row className="">
                    <Col md={12}>
                      <div
                        className="border rounded-3 shadow-sm"
                        style={{
                          maxHeight: "497px",
                          overflowY: "auto",
                          backgroundColor: "#ffffff",
                        }}
                      >
                        <Table striped hover responsive className="mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th
                                className="text-center"
                                style={{ width: "60px" }}
                              >
                                #
                              </th>
                              <th className="text-center">Ticket Number</th>
                              <th
                                className="text-center"
                                style={{ width: "100px" }}
                              >
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {officeClearTickets.map((ticketNo, index) => (
                              <tr key={index} style={{ cursor: "pointer" }}>
                                <td className="text-center text-muted fw-bold">
                                  {index + 1}
                                </td>
                                <td className="text-center">
                                  <span
                                    className="badge bg-primary bg-gradient px-3 py-2"
                                    style={{
                                      fontSize: "0.9rem",
                                      fontWeight: "600",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    {ticketNo}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <span className="badge bg-success">
                                    <i className="fas fa-check me-1"></i>
                                    Cleared
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                  </Row>
                )}

                <hr className="my-4" />

                <Row>
                  <Col md={12}>
                    <h5 className="mb-3">Utilities Office Cleared</h5>
                    <div className="mb-2">
                      <span className="fw-bold">
                        Total Utilities Office Clears:{" "}
                        {utilitiesOfficeCleared.length}
                      </span>
                    </div>
                    {utilitiesOfficeCleared.length > 0 ? (
                      <div
                        className="border rounded-3 shadow-sm"
                        style={{
                          maxHeight: "420px",
                          overflowY: "auto",
                          backgroundColor: "#ffffff",
                        }}
                      >
                        <Table striped hover responsive className="mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th
                                className="text-center"
                                style={{ width: "60px" }}
                              >
                                #
                              </th>
                              <th className="text-center">Ticket #</th>
                              <th
                                className="text-center"
                                style={{ width: "120px", whiteSpace: "nowrap" }}
                              >
                                Station Code
                              </th>
                              <th
                                className="text-center"
                                style={{ width: "110px" }}
                              >
                                Utility
                              </th>
                              <th className="text-center">Status</th>
                              <th className="text-center">Cleared At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {utilitiesOfficeCleared.map((row, idx) => (
                              <tr
                                key={`${row.ticketNo}-${row.utilityType}-${idx}`}
                              >
                                <td className="text-center text-muted fw-bold">
                                  {idx + 1}
                                </td>
                                <td className="text-center">
                                  {row.ticketNo ?? "-"}
                                </td>
                                <td
                                  className="text-center"
                                  style={{ width: "120px", whiteSpace: "nowrap" }}
                                >
                                  {row.stationCode ?? "-"}
                                </td>
                                <td
                                  className="text-center"
                                  style={{ width: "20px" }}
                                >
                                  {row.utilityType ?? "-"}
                                </td>
                                <td className="text-center">
                                  {row.status ?? "-"}
                                </td>
                                <td className="text-center">
                                  {row.createdAt
                                    ? moment(
                                        new Date(row.createdAt).toLocaleString(
                                          "en-US",
                                          { timeZone: "America/Toronto" },
                                        ),
                                      ).format("DD MMM YYYY hh:mm A")
                                    : "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <p className="text-muted mb-0">
                        No utilities office cleared in selected date range.
                      </p>
                    )}
                  </Col>
                </Row>
              </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
              <div>
                {officeClearTickets.length > 0 && (
                  <Button
                    variant="success"
                    onClick={exportTicketsToExcel}
                    className="me-2"
                  >
                    <i className="fas fa-download me-2"></i>
                    Export Tickets
                  </Button>
                )}
              </div>
              <Button
                variant="outline-primary"
                onClick={() => {
                  setOfficeClearModalShow(false);
                  setOfficeClearTickets([]);
                }}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            show={driveWayCurboxModalShow}
            onHide={() => setDriveWayCurboxModalShow(false)}
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                Driveway Curbox - {selectedUser?.label}{" "}
                {endDate && startDate && (
                  <>
                    ({formatDate(new Date(endDate), "dd/MM/yyyy")} -{" "}
                    {formatDate(new Date(startDate), "dd/MM/yyyy")})
                  </>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="border rounded p-3">
                {driveWayCurboxData.length > 0 ? (
                  <div
                    className="border rounded-3 shadow-sm"
                    style={{
                      maxHeight: "400px",
                      overflowY: "auto",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <Table striped hover responsive className="mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th className="text-center" style={{ width: "60px" }}>
                            #
                          </th>
                          <th className="text-center">Ticket Number</th>
                          <th className="text-center">Driveway Curbox</th>
                          <th className="text-center">Resurfaced</th>
                        </tr>
                      </thead>
                      <tbody>
                        {driveWayCurboxData.map((row, index) => (
                          <tr key={index}>
                            <td className="text-center text-muted fw-bold">
                              {index + 1}
                            </td>
                            <td className="text-center">
                              <span className="badge bg-primary bg-gradient px-3 py-2">
                                {row.ticketNumber ?? "-"}
                              </span>
                            </td>
                            <td className="text-center">
                              {row.driveWayCurbox ?? "-"}
                            </td>
                            <td className="text-center">
                              {row.resurfaced ?? "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-muted mb-0 text-center py-4">
                    No driveway curbox data for this period. Run Search with
                    Halton Region selected to load data.
                  </p>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-primary"
                onClick={() => setDriveWayCurboxModalShow(false)}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </Card>
      </section>
    </>
  );
};

export default Payroll;
